import {
	CartItemError,
	CommissionsType,
	Decimal,
	Id,
	Index,
	Maybe,
	RosterEntry,
	Timestamp,
	UserError,
} from '../data.js';
import { DomainId } from './domains.js';
import { decimal, ensure, isCartItemError, isSome, keepExisting, makeIndex, makeSum, maybe, num } from '../util.js';
import {
	Design,
	DesignPart,
	DesignRosterFields,
	ProductTemplate,
	ProductTemplateInventory,
	Template,
	Upload,
} from './designs.js';
import { Payment } from './payments.js';
import { Formula, FormulaInputs } from '../../admin/lib/pricing.js';
import { AddressInfo } from './address.js';
import { PublicStore, Store, StoreAutofillKit, StoreCollection, StoreProduct } from './stores.js';
import { Product, PublicProduct, WithFeatures } from './products.js';
import { ShippingOption } from './shipments.js';
import { Flow, PublicFlow } from './flows.js';
import { OrderItem } from './orders.js';
import { User } from './users.js';
import { getDesignRosterFields } from './roster.js';
import { Account, AccountRelation } from './accounts.js';
import { Promocode } from './promocodes.js';
import { Entity } from './entities.js';
import { CurrencyId } from './countries.js';
import { ColorTuple } from './colors.js';
import dayjs, { Dayjs } from 'dayjs';

export type SpecialItemType = null | 'discount' | 'credit' | 'extra' | 'tax' | 'shipping' | 'payment';

export interface ConvertedItemPrice {

	price?: Maybe<Decimal>;
	total: Decimal;
	effective_cost?: Decimal;
	
	// Not stored, added in breakdown
	totalWithExtras?: number;
	finalTotal?: number;
	finalPrice?: number;
	
}

export interface CartItemBase<I extends string | number = string | number> {
	
	id: I;
	parent_item?: Maybe<I>;
	
	name?: Maybe<string>;
	special?: SpecialItemType;
	product?: Id<Product> | null;
	
	preview?: string; // Deprecated for products, still in use for uploads.
	upload?: Maybe<Id<Upload>>;
	
	options?: ProductItemOptions | DiscountItemOptions | CreditItemOptions | ExtraItemOptions | TaxItemOptions | ShippingOption | PaymentItemOptions;
	
	product_template?: Maybe<string>;
	store_product?: Id<StoreProduct>;
	store?: Id<Store>;
	
	price?: Maybe<Decimal>;
	quantity?: Maybe<number>;
	total: Decimal;
	
	/** Should be present if Order has currency set */
	converted?: Maybe<ConvertedItemPrice>;
	
	weight_oz?: Maybe<Decimal>;
	
	sort?: number;
	updated?: Timestamp;
	
	design?: Maybe<string>;
	roster?: Maybe<RosterEntry[]>;
	
	payment?: Maybe<string>;
	
	// virtual
	product_info?: Maybe<PublicProduct & WithFeatures>;
	product_template_info?: Maybe<ProductTemplate>;
	flow_info?: Maybe<PublicFlow>;
	store_product_info?: StoreProduct;
	design_info?: Maybe<Design>;
	payment_info?: Maybe<Payment>;
	store_info?: Maybe<PublicStore>;
	hidden?: Maybe<boolean>;
	
}

export interface CartItem extends CartItemBase<string & { __entity?: 'CartItem' }> {
	
	id: string;
	cart?: string;
	
	user?: Maybe<Id<User>>;
	session?: string;
	
	created?: Timestamp;
	deleted?: Timestamp;
	
	no_promocode?: boolean;
	effective_cost?: Decimal;
	
	editable?: boolean;
	removable?: boolean;
	
	product_template_inventory?: Maybe<ProductTemplateInventory>;
}

export type ItemCommission = {
	type: CommissionsType;
	relation: Maybe<Id<AccountRelation>>;
	amount: Decimal;
	base: Decimal;
};
export type ItemCommissionMap = Record<Id<Account>, ItemCommission>;

export type InternalCartItem = CartItem & {
	commissions?: Maybe<ItemCommissionMap>;
	product_info?: Maybe<Product>;
	store_info?: Maybe<Store>;
	price_breakdown?: Maybe<DesignPriceBreakdown>;
	store_price_breakdown?: Maybe<DesignPriceBreakdown>;
	discount_formula?: Maybe<Formula>;
	discount_formula_inputs?: Maybe<FormulaInputs>;
	use_store_lowest_price?: Maybe<boolean>;
};

export type CartProductItem<T extends CartItemBase<any> = CartItem> = T & {
	product: Id<Product>;
	special?: null;
	options: Maybe<ProductItemOptions>;
	product_info: PublicProduct;
	flow_info: Flow;
};

export interface ProductItemOptions {
	fit?: string;
	comment?: string;
	products?: Id<Product>[];
	kits?: Id<StoreAutofillKit>[];
	templates?: Id<Template>[];
	productTemplate?: Id<ProductTemplate>; // Do not use in OrderItem, use product_template instead!
	colors?: DesignPart['colors'];
	threads?: DesignPart['threads'];
	colorList?: Maybe<Maybe<ColorTuple>[]>;
	stockColorList?: Maybe<Maybe<ColorTuple>[]>;
	createdGiftCards?: string[]; // broken, should fix probably
	uploads?: Upload['id'][];
}

export type CartDiscountItem<T extends CartItemBase = CartItem> = T & {
	special: 'discount';
	product?: null;
	options?: DiscountItemOptions;
};

export interface DiscountItemOptions {
	type: string;
	promocode?: string;
}

export type CartCreditItem<T extends CartItemBase = CartItem> = T & {
	special: 'credit';
	product?: null;
	options?: CreditItemOptions;
};

export interface CreditItemOptions {
	type: string;
	promocode?: string;
}

export type CartPaymentItem<T extends CartItemBase = CartItem> = T & {
	special: 'payment';
	product?: null;
	options?: PaymentItemOptions;
	payment: string;
};

/** @deprecated moved to payment */
export interface PaymentItemOptions {
	type: 'purchase_order' | 'stripe' | 'paypal' | string;
	reference?: string;
	paypalOrderId?: string;
	paypalCaptureId?: string;
	stripePaymentIntent?: string;
}

export type CartExtraItem<T extends CartItemBase = CartItem> = T & {
	special: 'extra';
	product?: null;
	options?: ExtraItemOptions;
};

export interface ExtraItemOptions {
	kind: string; // e.g. decoration
	type: string; // e.g. embroidery
	price?: Decimal;
	quantityPerItem?: number;
	perItem?: Decimal;
}

export type CartTaxItem<T extends CartItemBase = CartItem> = T & {
	special: 'tax';
	product?: null;
	options?: TaxItemOptions;
};

export interface TaxItemOptions {
	type: string;
	rate: Decimal;
}

export type CartShippingItem<T extends CartItemBase = CartItem> = T & {
	special: 'shipping';
	product?: null;
	options: ShippingOption;
};
/**
 * Cart totals!
 * Also used for order totals.
 * Negative ones are negative, everything is to be summed up with +
 */
export type CartTotals<T> = {
	
	/**
	 * Inital total of all the actual products in cart, without any discounts
	 * (but with extras and special pricing where applicable)
	 * Also excludes gift cards.
	 */
	product: T;
	
	/**
	 * Selected shipping option cost
	 */
	shipping: T;
	
	/**
	 * Sum of discounts applying to specific products
	 * (negative)
	 */
	productDiscount: T;
	
	/**
	 * Any discounts not for specific products
	 * (this is *only* custom items now, everything else is broken up)
	 * (negative)
	 */
	otherDiscount: T;
	
	/**
	 * productDiscount + otherDiscount
	 */
	discount: T;
	
	/**
	 * productTotal + productDiscountTotal + otherDiscountTotal
	 */
	effectiveProduct: T;
	
	/**
	 * `totals.effectiveProduct` + `shippingOption.amount`
	 * What they're "paying" us, whether with money or some kind of credit.
	 * This is what the tax is applied to!
	 */
	effective: T;
	
	/**
	 * Taxes, if any
	 */
	tax: T;
	/**
	 * effective + tax
	 */
	withTax: T;
	
	/**
	 * Any gift cards being bought, mostly, but can be used for some kind of prepaid credit later.
	 * Not taxed, not subject to discounts or gift card deduction.
	 */
	prepayment: T;
	
	/**
	 * Amount paid with gift card, store credit, some kind of a PO, etc.
	 * (negative)
	 */
	credit: T;
	/**
	 * totalWithTax + creditTotal. What is left for the client to pay, if anything.
	 */
	payable: T;
	/**
	 * Sum of payments already made
	 * (negative)
	 */
	payments: T;
	/**
	 * payable + payments
	 */
	remaining: T;
	
	/**
	 * amount of store credit available to cover the remaining amount. *does* include already used amount, if any.
	 * (positive, unused in totals)
	 */
	storeCreditAvailable?: T;
	
	purchaseOrderLimit?: T;
	
};

/**
 * What is actually stored in the table
 */
export interface CartData {
	
	id: string & { __entity?: 'Cart' };
	domain: DomainId;
	user: Maybe<Id<User>>;
	account: Maybe<Id<Account>>;
	session: Maybe<string>;
	
	created: Timestamp;
	updated: Timestamp;
	deleted: Maybe<Timestamp>;
	update_count: number;
	
	billing_address: Maybe<AddressInfo>;
	shipping_address: Maybe<AddressInfo>;
	promocodes: string[];
	shortlink: Maybe<string>;
	
	available_shipping_options: Maybe<ShippingOption[]>;
	shipping_option: Maybe<ShippingOption>;
	use_store_credit: boolean;
	order_comment: Maybe<string>;
	
	collection_access: Maybe<Record<StoreCollection['id'], string>>;
	name: Maybe<string>;
	delete_on_order: Maybe<boolean>;
	
	totals: Maybe<CartTotals<Decimal>>;
	
}

export type SavedCartEntry = Pick<Cart, 'id' | 'name' | 'updated'> & { total: Maybe<Decimal> };

export type CartDataEntity = Entity<'carts'>;

export interface Cart<T extends CartItemBase = CartItem> extends CartData {
	
	items: T[];
	
	totals: CartTotals<Decimal>;
	currency: Maybe<CurrencyId>;
	currency_rate: Maybe<Decimal>;
	converted: Maybe<CartTotals<Decimal>>;
	
	weightOz: Decimal;
	freeShipping: boolean;
	pickUpOption: boolean;
	
	errors: UserError[];
	empty: boolean;
	physical: boolean;
	valid: boolean;
	ready: boolean;
	paymentNeeded: boolean;
	
	store: Maybe<PublicStore>;
	savedCarts: Maybe<SavedCartEntry[]>;
	
	isExpanded?: boolean;
	
}

export type ExpandedCartItem<T extends CartItemBase = CartItem> = T & {
	errors?: CartItemError[];
	rootItem: Maybe<Id<T>>;
	subItems: Id<T>[];
	allSubItems: Id<T>[];
	discounts: CartDiscountItem<ExpandedCartItem<T>>[];
	extras: CartExtraItem<ExpandedCartItem<T>>[];
	totalWithExtras: number;
	finalPrice: number;
	finalTotal: number;
	rosterFields: Maybe<DesignRosterFields>;
	giftCards?: Maybe<Promocode[]>;
};

export interface ItemsBreakdown<T extends CartItemBase> {
	items: T[];
	itemMap: Index<T>;
	products: CartProductItem<T>[];
	topProducts: CartProductItem<T>[];
	discounts: CartDiscountItem<T>[];
	productDiscounts: CartDiscountItem<T>[];
	otherDiscounts: CartDiscountItem<T>[]; // excludes product discounts
	discountTotals: CartDiscountItem<T>[];
	extras: CartExtraItem<T>[];
	credits: CartCreditItem<T>[];
	payments: CartPaymentItem<T>[]; // filled in after order creation
	taxes: CartTaxItem<T>[];
	shippingItems: CartShippingItem<T>[];
	lastShipDate: Maybe<Timestamp>;
}

export type ExpandedCart<I extends CartItemBase = CartItem> = Omit<Cart<I>, 'items'> & ItemsBreakdown<ExpandedCartItem<I>> & {
	isExpanded: true;
	fatalErrors: UserError[];
};

export const isExpandedCart = <I extends CartItemBase = CartItem> (cart: Cart<I> | ExpandedCart<I>): cart is ExpandedCart<I> => !!cart.isExpanded;

export interface DesignPriceBreakdown<I extends CartItemBase = CartItemBase<string>> {
	
	/**
	 * Actual quantity of product to expect.
	 */
	quantity: number;
	
	/**
	 * Apparel price per item, sans extras/discounts.
	 */
	apparelPrice: Decimal;
	/**
	 * Apparel price * quantity, sans extras/discounts.
	 */
	apparelTotal: Decimal;// without discounts/extras
	
	/**
	 * List of extras
	 */
	extras: CartExtraItem<I>[];
	extrasTotal: Decimal;
	
	/**
	 * List of discounts.
	 */
	discounts: CartDiscountItem<I>[];
	discountTotal: Decimal;
	
	/**
	 * Apparel plus extras/discounts.
	 */
	productTotal: Decimal;
	
	/**
	 * Additional services unaffected by quantity. E.g. vector/digitiZe
	 */
	services: CartProductItem<I>[];
	
	/**
	 * Total of product + services
	 */
	total: Decimal;
	/**
	 * Total divided by quantity
	 */
	each: Decimal;
	
	errors: UserError[];
	
}

export const initCartTotals = (): CartTotals<number> => ({
	product: 0,
	productDiscount: 0,
	otherDiscount: 0,
	discount: 0,
	shipping: 0,
	effectiveProduct: 0,
	effective: 0,
	tax: 0,
	withTax: 0,
	prepayment: 0,
	credit: 0,
	payments: 0,
	payable: 0,
	remaining: 0,
	storeCreditAvailable: 0,
	purchaseOrderLimit: 0,
});

export const isProductItem = <T extends CartItemBase>(item: T): item is CartProductItem<T> => !!item.product;
export const isDiscountItem = <T extends CartItemBase>(item: T): item is CartDiscountItem<T> => item.special == 'discount';
export const isCreditItem = <T extends CartItemBase>(item: T): item is CartCreditItem<T> => item.special == 'credit';
export const isPaymentItem = <T extends CartItemBase>(item: T): item is CartPaymentItem<T> => item.special == 'payment';
export const isExtraItem = <T extends CartItemBase>(item: T): item is CartExtraItem<T> => item.special == 'extra';
export const isShippingItem = <T extends CartItemBase>(item: T): item is CartShippingItem<T> => item.special == 'shipping';
export const isTaxItem = <T extends CartItemBase>(item: T): item is CartTaxItem<T> => item.special == 'tax';

/**
 * Whether isProductItem but not isPrepaymentProductItem.
 * @param item
 */
export const isPrepaymentProductItem = <T extends CartItemBase>(item: T): item is CartProductItem<T> & { product: 'gift' } => item.product == 'gift';

/**
 * Whether isProductItem but not isPrepaymentItem.
 * @param item
 */
export const isNormalProductItem = <T extends CartItemBase>(item: T): item is CartProductItem<T> => isProductItem(item) && !isPrepaymentProductItem(item);

/**
 * Whether this is an item that should be included in a product's effective_cost sum.
 * @param item
 */
export const isProductCostItem = <T extends CartItemBase>(item: T): item is (CartProductItem<T> | CartDiscountItem<T> | CartShippingItem<T>) => isNormalProductItem(item) || isDiscountItem(item) || isExtraItem(item);

export const isOrderItem = (item: CartItem | OrderItem): item is OrderItem => isSome((item as OrderItem).order);

export function breakDownItems<T extends CartItemBase>(itemsIn: T[]): ItemsBreakdown<ExpandedCartItem<T>> {

	const items = itemsIn.map(it => ({
		...it,
		parentItem: null,
		rootItem: null,
		subItems: [],
		allSubItems: [],
		discounts: [],
		extras: [],
		totalWithExtras: Number(it.total),
		finalTotal: Number(it.total),
		finalPrice: Number(it.price),
		rosterFields: (it.product_info && it.design_info?.parts && getDesignRosterFields(it.product_info, it.design_info.parts, it.store_product_info, 'allowed')) ?? null,
	}) as ExpandedCartItem<T>);
	
	const itemMap = makeIndex(items);
	
	const discountTotals: Record<string, CartDiscountItem<ExpandedCartItem<T>>> = {};
	
	for (const item of items) {
	
		const p = item.parent_item ? itemMap[item.parent_item as T['id']] : null;
		if (p) {
		
			p.subItems.push(item.id);
			
			if (isDiscountItem(item)) {
				p.discounts.push(item);
			}
			
			if (isExtraItem(item)) p.extras.push(item);
			
			let anc: Maybe<ExpandedCartItem<T>> = p;
			while (anc) {
				item.rootItem = anc.id;
				anc.allSubItems.push(item.id);
				anc = maybe(anc.parent_item, pi => itemMap[pi as T['id']]);
			}
			
		}
		
		if (isDiscountItem(item)) {
			const key = keepExisting([item.options?.type, item.options?.promocode]).join(':') || item.name || item.id;
			const ex = discountTotals[key];
			if (ex) ex.total = decimal(num(ex.total) + num(item.total));
			else discountTotals[key] = { ...item };
		}
		
	}
	
	for (const item of items) {
		item.totalWithExtras = Number(item.total) + item.extras.reduce((acc, it) => acc + Number(it.total), 0);
		item.finalTotal = Number(item.total) + item.allSubItems.reduce((acc, iid) => acc + Number(itemMap[iid].total), 0);
		item.finalPrice = item.finalTotal / (item.quantity || 1);
	}
	
	const discounts = items.filter(isDiscountItem);
	const products = items.filter(isProductItem).filter(it => !it.hidden);
	
	let shipDate: Maybe<Dayjs> = null;
	for (const it of products) {
		const days = it.flow_info.max_prod_days;
		if (!days) continue;
		const date = dayjs().add(days, 'days');
		shipDate = shipDate && shipDate.isAfter(date) ? shipDate : date;
	}
	
	return {
		items,
		itemMap,
		products,
		topProducts: products.filter(p => !p.parent_item),
		discounts,
		productDiscounts: discounts.filter(it => !!it.parent_item),
		otherDiscounts: discounts.filter(it => !it.parent_item),
		discountTotals: Object.values(discountTotals),
		extras: items.filter(isExtraItem),
		credits: items.filter(isCreditItem),
		taxes: items.filter(isTaxItem),
		payments: items.filter(isPaymentItem),
		shippingItems: items.filter(isShippingItem),
		lastShipDate: shipDate?.format(),
	};
	
}

export function expandCart<I extends CartItemBase = CartItem>(data: Cart<I>): ExpandedCart<I> {

	if (isExpandedCart(data)) return data;
	
	let itemsIn = data.items;
	if (data.converted) {
		itemsIn = itemsIn.map(it => ({ ...it, ...ensure(it.converted), converted: null }));
	}

	const breakdown = breakDownItems(itemsIn);
	const { items, itemMap } = breakdown;
	const itemErrors = data.errors.filter(isCartItemError);
	
	for (const item of items) {
		item.errors = itemErrors.filter(e => e.item == item.id);
	}
	
	return {
		...data,
		...breakdown,
		totals: data.converted ?? data.totals,
		fatalErrors: data.errors.filter(e => !e.continue),
		isExpanded: true,
	};
	
}

export function getCartName(cart: Pick<CartData, 'name'>) {
	return cart.name ?? 'Cart';
}

export const maybeConvertableCurrency = (cid: Maybe<CurrencyId>) => cid && cid != 'USD' ? cid : null;

export const sumItems = makeSum<CartItemBase>('total') as (<T extends CartItemBase> (
	items: T[],
	key?: keyof T,
) => number);

export const sumConvertedItems = <T extends CartItemBase> (items: T[], key: keyof ConvertedItemPrice = 'total') => num(
	items.reduce((a, it) => a + num(it.converted![key] as any ?? 0), 0),
);
